import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Typography,
  IconButton,
  Grid,
  CircularProgress,
} from '@mui/material';
import { Image as ImageIcon, Delete as DeleteIcon } from '@mui/icons-material';
import ColorPicker from './ColorPicker';

function CardForm({ open, onClose, onSubmit, initialCard = null }) {
  // Ajout de la fonction pour obtenir l'URL complète
  const getImageUrl = (imageUrl) => {
    if (!imageUrl) return '';
    const baseUrl = process.env.REACT_APP_API_URL.replace(/\/api$/, '');
    return `${baseUrl}${imageUrl}`;
  };

  const [card, setCard] = useState({
    front: initialCard?.front || '',
    back: initialCard?.back || '',
    frontColor: initialCard?.frontColor || '#FFFFFF',
    backColor: initialCard?.backColor || '#FFFFFF',
    frontImage: null,
    backImage: null,
    // Utiliser getImageUrl pour les prévisualisations
    frontImagePreview: getImageUrl(initialCard?.frontImageUrl) || '',
    backImagePreview: getImageUrl(initialCard?.backImageUrl) || ''
  });

  // Réinitialiser le formulaire quand initialCard change
  useEffect(() => {
    if (initialCard) {
      setCard({
        front: initialCard.front || '',
        back: initialCard.back || '',
        frontColor: initialCard.frontColor || '#FFFFFF',
        backColor: initialCard.backColor || '#FFFFFF',
        frontImage: null,
        backImage: null,
        // Utiliser getImageUrl pour les prévisualisations
        frontImagePreview: getImageUrl(initialCard.frontImageUrl) || '',
        backImagePreview: getImageUrl(initialCard.backImageUrl) || ''
      });
    }
  }, [initialCard]);

  // État séparé pour chaque côté
  const [uploadingState, setUploadingState] = useState({
    front: false,
    back: false
  });

  const handleImageChange = async (event, side) => {
    const file = event.target.files[0];
    if (!file) return;

    // Vérifier la taille du fichier
    if (file.size > 5000000) {
      alert('L\'image est trop volumineuse. Veuillez choisir une image de moins de 5MB.');
      return;
    }

    // Mettre à jour l'état de chargement immédiatement
    setUploadingState(prev => ({ ...prev, [side]: true }));

    // Créer un blob URL pour la prévisualisation immédiate
    const objectUrl = URL.createObjectURL(file);
    setCard(prev => ({
      ...prev,
      [`${side}ImagePreview`]: objectUrl,
      [`${side}Image`]: file // Stocker le fichier immédiatement
    }));

    // Si l'image est petite, terminer ici
    if (file.size < 500000) {
      setUploadingState(prev => ({ ...prev, [side]: false }));
      return;
    }

    try {
      // Compression en arrière-plan
      const worker = new Worker('/imageCompressor.worker.js');
      
      const timeoutId = setTimeout(() => {
        worker.terminate();
        setUploadingState(prev => ({ ...prev, [side]: false }));
      }, 5000);

      worker.onmessage = (e) => {
        clearTimeout(timeoutId);
        if (!e.data.error) {
          setCard(prev => ({
            ...prev,
            [`${side}Image`]: e.data.compressedFile
          }));
        }
        setUploadingState(prev => ({ ...prev, [side]: false }));
        worker.terminate();
      };

      worker.onerror = () => {
        clearTimeout(timeoutId);
        setUploadingState(prev => ({ ...prev, [side]: false }));
        worker.terminate();
      };

      worker.postMessage({ file });
    } catch (error) {
      console.error('Erreur lors de la compression:', error);
      setUploadingState(prev => ({ ...prev, [side]: false }));
    }
  };

  // Nettoyer les URLs d'objets lors du démontage
  useEffect(() => {
    return () => {
      if (card.frontImagePreview && card.frontImagePreview.startsWith('blob:')) {
        URL.revokeObjectURL(card.frontImagePreview);
      }
      if (card.backImagePreview && card.backImagePreview.startsWith('blob:')) {
        URL.revokeObjectURL(card.backImagePreview);
      }
    };
  }, [card.frontImagePreview, card.backImagePreview]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await onSubmit(card);
      
      // Ne réinitialiser que si c'est une nouvelle carte
      if (!initialCard) {
        // Garder les couleurs de la dernière carte
        const { frontColor, backColor } = card;
        setCard({
          front: '',
          back: '',
          frontColor,  // Conserver la couleur du recto
          backColor,   // Conserver la couleur du verso
          frontImage: null,
          backImage: null,
          frontImagePreview: '',
          backImagePreview: ''
        });
      }
      
      // Fermer le formulaire après une modification réussie
      if (initialCard) {
        handleClose();
      }
    } catch (error) {
      console.error('Erreur lors de la soumission:', error);
    }
  };

  const handleClose = () => {
    setCard({
      front: '',
      back: '',
      frontColor: '#FFFFFF',
      backColor: '#FFFFFF',
      frontImage: null,
      backImage: null,
      frontImagePreview: '',
      backImagePreview: ''
    });
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {initialCard ? 'Modifier la carte' : 'Nouvelle carte'}
      </DialogTitle>
      <DialogContent>
        <Grid container spacing={3}>
          {/* Recto */}
          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom>
              Recto
            </Typography>
            <TextField
              fullWidth
              multiline
              rows={3}
              label="Texte du recto"
              value={card.front}
              onChange={(e) => setCard({ ...card, front: e.target.value })}
              margin="normal"
            />
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                Couleur du texte
              </Typography>
              <ColorPicker
                color={card.frontColor}
                onChange={(color) => setCard({ ...card, frontColor: color })}
              />
            </Box>
            <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
              <input
                type="file"
                accept="image/*"
                style={{ display: 'none' }}
                id="front-image"
                onChange={(e) => handleImageChange(e, 'front')}
              />
              <label htmlFor="front-image">
                <Button
                  variant="outlined"
                  component="span"
                  startIcon={uploadingState.front ? <CircularProgress size={20} /> : <ImageIcon />}
                  disabled={uploadingState.front}
                >
                  {uploadingState.front ? 'Compression...' : 
                    (card.frontImage || card.frontImagePreview ? 'Changer l\'image' : 'Ajouter une image')}
                </Button>
              </label>
              {(card.frontImage || card.frontImagePreview) && (
                <>
                  <IconButton 
                    color="error" 
                    onClick={() => setCard({ 
                      ...card, 
                      frontImage: null,
                      frontImagePreview: '' 
                    })}
                  >
                    <DeleteIcon />
                  </IconButton>
                  <Box
                    component="img"
                    src={card.frontImagePreview}
                    alt="Preview"
                    sx={{
                      maxWidth: 100,
                      maxHeight: 100,
                      objectFit: 'contain'
                    }}
                  />
                </>
              )}
            </Box>
          </Grid>

          {/* Verso */}
          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom>
              Verso
            </Typography>
            <TextField
              fullWidth
              multiline
              rows={3}
              label="Texte du verso"
              value={card.back}
              onChange={(e) => setCard({ ...card, back: e.target.value })}
              margin="normal"
            />
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                Couleur du texte
              </Typography>
              <ColorPicker
                color={card.backColor}
                onChange={(color) => setCard({ ...card, backColor: color })}
              />
            </Box>
            <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
              <input
                type="file"
                accept="image/*"
                style={{ display: 'none' }}
                id="back-image"
                onChange={(e) => handleImageChange(e, 'back')}
              />
              <label htmlFor="back-image">
                <Button
                  variant="outlined"
                  component="span"
                  startIcon={uploadingState.back ? <CircularProgress size={20} /> : <ImageIcon />}
                  disabled={uploadingState.back}
                >
                  {uploadingState.back ? 'Compression...' : 
                    (card.backImage || card.backImagePreview ? 'Changer l\'image' : 'Ajouter une image')}
                </Button>
              </label>
              {(card.backImage || card.backImagePreview) && (
                <>
                  <IconButton 
                    color="error" 
                    onClick={() => setCard({ 
                      ...card, 
                      backImage: null,
                      backImagePreview: '' 
                    })}
                  >
                    <DeleteIcon />
                  </IconButton>
                  <Box
                    component="img"
                    src={card.backImagePreview}
                    alt="Preview"
                    sx={{
                      maxWidth: 100,
                      maxHeight: 100,
                      objectFit: 'contain'
                    }}
                  />
                </>
              )}
            </Box>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Annuler</Button>
        <Button 
          onClick={handleSubmit}
          variant="contained"
          disabled={
            uploadingState.front || uploadingState.back ||
            (!card.front && !card.frontImage && !card.frontImagePreview) ||
            (!card.back && !card.backImage && !card.backImagePreview)
          }
        >
          {uploadingState.front || uploadingState.back ? 'Compression...' : (initialCard ? 'Modifier' : 'Créer')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default CardForm; 